import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { scrollToSection, scrollToTop } from '../utils/scrollToSection';
import ContactActions from './ContactActions';
import ThemeSwitch from './ThemeSwitch';
import DftMark from './DftMark';

const navItems = ['Services', 'Integrations', 'Industries', 'Why Us', 'Blog', 'Contact'];

export default function Navbar() {
  const { colors, theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [ctaHover, setCtaHover] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [logoHovered, setLogoHovered] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const contactRef = useRef(null);

  const premiumBlue = '#6366f1';

  const styles = {
    nav: {
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 5vw', height: '70px',
      gap: 'clamp(0.75rem, 1.5vw, 1.5rem)',
      borderBottom: `1px solid ${colors.border}`,
      transition: 'background 0.3s',
    },
    logo: {
      fontFamily: "'Inter', sans-serif", fontWeight: 800,
      fontSize: 'clamp(1.4rem, 1.7vw, 1.8rem)', letterSpacing: '3px', color: colors.text,
      cursor: 'pointer', background: 'none', border: 'none',
      whiteSpace: 'nowrap', flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', gap: '0.45em',
    },
    logoAccent: { color: colors.text },
    navLinks: {
      display: 'flex', gap: 'clamp(0.9rem, 1.5vw, 2rem)', listStyle: 'none',
    },
    navLink: {
      color: colors.muted, fontSize: 'clamp(0.72rem, 0.85vw, 0.82rem)', fontWeight: 500,
      whiteSpace: 'nowrap',
      letterSpacing: '1px', textTransform: 'uppercase',
      cursor: 'pointer', transition: 'all 0.2s', textDecoration: 'none',
      display: 'inline-block',
    },
    // "Get in Touch": filled accent + glow so it's the most prominent thing in the bar
    cta: {
      background: colors.accentBg, color: colors.accentText,
      border: `2px solid ${colors.accentBg}`,
      padding: '0.72rem clamp(1.1rem, 1.5vw, 1.6rem)',
      fontSize: 'clamp(0.84rem, 0.95vw, 0.95rem)', fontWeight: 700,
      whiteSpace: 'nowrap', flexShrink: 0,
      letterSpacing: '1px', textTransform: 'uppercase',
      cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', textDecoration: 'none',
      display: 'inline-flex', alignItems: 'center', gap: '0.55rem',
      fontFamily: "'Inter', sans-serif",
    },
    rightGroup: { display: 'flex', alignItems: 'center', gap: 'clamp(0.75rem, 1.2vw, 1.25rem)', flexShrink: 0 },
    contactPanel: {
      position: 'absolute', right: 0, top: 'calc(100% + 14px)',
      width: '300px', maxWidth: 'calc(100vw - 2rem)', padding: '1rem',
      background: colors.surface, border: `1px solid ${colors.border}`,
      borderRadius: '12px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
      animation: 'fadeUp 0.2s ease',
    },
    contactTitle: { fontSize: '0.95rem', fontWeight: 700, color: colors.text, marginBottom: '0.8rem' },
    hamburger: {
      display: 'none', flexDirection: 'column', gap: '5px', cursor: 'pointer',
      background: 'none', border: 'none', padding: '4px',
    },
    hamburgerBar: { width: '24px', height: '2px', background: colors.text, display: 'block' },
    mobileOverlay: {
      position: 'fixed', inset: 0, background: colors.bg, zIndex: 99,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: '2rem',
    },
    mobileLink: {
      fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: '2.8rem',
      letterSpacing: '3px', color: colors.text, cursor: 'pointer',
      transition: 'color 0.2s',
    },
    closeBtn: {
      position: 'absolute', top: '1.5rem', right: '5vw',
      fontSize: '2rem', cursor: 'pointer', color: colors.muted,
      background: 'none', border: 'none',
    },
  };

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Close the mobile menu and contact panel on route change.
  useEffect(() => {
    setMobileOpen(false);
    setContactOpen(false);
  }, [location.pathname]);

  // Close the "Get in touch" panel on an outside click or Escape.
  useEffect(() => {
    if (!contactOpen) return undefined;
    const onPointerDown = (e) => {
      if (!contactRef.current?.contains(e.target)) setContactOpen(false);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') setContactOpen(false); };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [contactOpen]);

  useEffect(() => {
    // Scroll events fire many times per frame; coalesce them so the section
    // lookup (which forces a layout read) runs at most once per frame.
    let frame = 0;
    const update = () => {
      frame = 0;
      setScrolled(window.scrollY > 50);

      if (location.pathname !== '/') return;

      // Track active section (home page only)
      const sections = navItems.map(item => {
        const id = sectionId(item);
        return { id, element: document.getElementById(id) };
      });

      let current = 'home';
      for (const { id, element } of sections) {
        if (element && element.offsetTop - 100 <= window.scrollY) {
          current = id;
        }
      }
      setActiveSection(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const sectionId = (label) => {
    const map = {
      'Services': 'services', 'Integrations': 'integrations', 'Industries': 'industries',
      'Why Us': 'why-us', 'Blog': 'blog', 'Clients': 'testimonials', 'Contact': 'contact',
    };
    return map[label] || label.toLowerCase();
  };

  const goToSection = (id) => (e) => {
    e.preventDefault();
    setMobileOpen(false);
    if (location.pathname === '/') {
      scrollToSection(id);
    } else {
      navigate(`/#${id}`);
    }
  };

  const goHome = () => {
    setMobileOpen(false);
    if (location.pathname === '/') {
      scrollToTop();
    } else {
      navigate('/');
    }
  };

  const navBg = theme === 'dark'
    ? (scrolled ? 'rgba(0,0,0,0.98)' : 'rgba(0,0,0,0.92)')
    : (scrolled ? 'rgba(255,255,255,0.98)' : 'rgba(255,255,255,0.92)');

  return (
    <>
      <nav style={{ ...styles.nav, background: navBg }}>
        <button
          style={styles.logo}
          className="nav-logo"
          onClick={goHome}
          aria-label="Go to home"
        >
          <DftMark height="1.05em" />
          <span>
            <span
              style={{ transition: 'color 0.3s ease', color: logoHovered === 'deck' ? premiumBlue : colors.text, cursor: 'pointer' }}
              onMouseEnter={() => setLogoHovered('deck')}
              onMouseLeave={() => setLogoHovered(null)}
            >
              Deck
            </span>
            {' '}
            <span
              style={{ transition: 'color 0.3s ease', color: logoHovered === 'for' ? premiumBlue : colors.text, cursor: 'pointer' }}
              onMouseEnter={() => setLogoHovered('for')}
              onMouseLeave={() => setLogoHovered(null)}
            >
              For
            </span>
            <span
              style={{
                ...styles.logoAccent,
                transition: 'color 0.3s ease',
                color: logoHovered === 'tech' ? premiumBlue : colors.text,
                cursor: 'pointer',
              }}
              onMouseEnter={() => setLogoHovered('tech')}
              onMouseLeave={() => setLogoHovered(null)}
            >
              {' '}Tech
            </span>
          </span>
        </button>

        <ul style={styles.navLinks} className="nav-desktop">
          {navItems.map(item => (
            <li key={item}>
              <a
                href={`/#${sectionId(item)}`}
                style={{
                  ...styles.navLink,
                  color: hoveredLink === item || activeSection === sectionId(item) ? colors.text : colors.muted,
                  transform: hoveredLink === item || activeSection === sectionId(item) ? 'scale(1.1)' : 'scale(1)',
                }}
                onMouseEnter={() => setHoveredLink(item)}
                onMouseLeave={() => setHoveredLink(null)}
                onClick={goToSection(sectionId(item))}
              >
                {item}
              </a>
            </li>
          ))}
        </ul>

        <div style={styles.rightGroup} className="nav-right">
          {/* Get in touch: Send Email / Call Now, details revealed on click */}
          <div ref={contactRef} style={{ position: 'relative' }}>
            <button
              type="button"
              style={{
                ...styles.cta,
                transform: ctaHover || contactOpen ? 'translateY(-1px)' : 'none',
                boxShadow: ctaHover || contactOpen
                  ? `0 8px 24px ${colors.accentBg}73`
                  : `0 4px 14px ${colors.accentBg}4d`,
              }}
              onMouseEnter={() => setCtaHover(true)}
              onMouseLeave={() => setCtaHover(false)}
              onClick={() => setContactOpen(open => !open)}
              aria-expanded={contactOpen}
              aria-haspopup="dialog"
              aria-label="Get in touch"
              className="nav-cta-btn"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
                <path d="M3.5 6.5l8.5 6.5 8.5-6.5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              </svg>
              <span className="nav-cta-label">Get in Touch</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                className="nav-cta-chevron"
                style={{ transform: contactOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                <path d="M5 9l7 7 7-7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {contactOpen && (
              <div style={styles.contactPanel} className="nav-contact-panel" role="dialog" aria-label="Get in touch">
                <div style={styles.contactTitle}>Get in touch</div>
                <ContactActions compact onDone={() => setContactOpen(false)} />
              </div>
            )}
          </div>

          <ThemeSwitch />

          <button
            style={{ ...styles.hamburger, display: 'none' }}
            className="hamburger-btn"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            aria-expanded={mobileOpen}
          >
            <span style={styles.hamburgerBar}></span>
            <span style={styles.hamburgerBar}></span>
            <span style={styles.hamburgerBar}></span>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div style={styles.mobileOverlay} role="dialog" aria-modal="true" aria-label="Mobile navigation">
          <button style={styles.closeBtn} onClick={() => setMobileOpen(false)} aria-label="Close menu">✕</button>
          {navItems.map(item => (
            <a
              key={item}
              href={`/#${sectionId(item)}`}
              style={styles.mobileLink}
              onClick={goToSection(sectionId(item))}
            >
              {item}
            </a>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginTop: '1.5rem', color: colors.muted, fontSize: '0.95rem' }}>
            Dark mode <ThemeSwitch />
          </div>
        </div>
      )}

      <style>{`
        /* Below this the full link row no longer fits on one line (tablets,
           small laptop windows) — collapse to the hamburger menu. */
        @media (max-width: 1180px) {
          .nav-desktop { display: none !important; }
          .hamburger-btn { display: flex !important; }
        }
        /* Get in touch: icon + label; icon only on very narrow screens */
        @media (max-width: 520px) {
          .nav-cta-label { display: none; }
        }
        /* Phones: tighter logo and spacing so logo, Get in Touch, theme switch
           and menu all fit; the contact panel spans the screen under the bar. */
        @media (max-width: 600px) {
          nav { gap: 0.6rem !important; }
          .nav-logo { font-size: clamp(1rem, 4.4vw, 1.4rem) !important; letter-spacing: 1.5px !important; padding: 0; }
          .nav-right { gap: 0.6rem !important; }
          .nav-cta-btn { padding: 0.55rem 0.7rem !important; }
          .nav-contact-panel {
            position: fixed !important; top: 78px !important;
            left: 12px !important; right: 12px !important; width: auto !important;
          }
        }
        @media (max-width: 400px) {
          .nav-cta-chevron { display: none; }
        }
      `}</style>
    </>
  );
}
